import { logger } from '@teko/log';
import { isValidPhoneFormat, isValidEmailFormat } from '@teko/utils';
import { useState, useEffect } from 'react';
import { t } from '../i18n.es.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
const filterLocations = (locations, limits) => {
  return limits && limits.length > 0 ? locations.filter((location) => limits.some((allowDis) => {
    const re = new RegExp(`^${allowDis}$`);
    return re.test(location.code);
  })) : locations;
};
const useLocations = (defaultAddress, locationService, limit = null) => {
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [wards, setWards] = useState([]);
  const [province, setProvince] = useState(defaultAddress.provinceCode ? { code: defaultAddress.provinceCode, name: defaultAddress.provinceName } : null);
  const [district, setDistrict] = useState(defaultAddress.districtCode ? { code: defaultAddress.districtCode, name: defaultAddress.districtName } : null);
  const [ward, setWard] = useState(defaultAddress.wardCode ? { code: defaultAddress.wardCode, name: defaultAddress.wardName } : null);
  useEffect(() => {
    locationService.getProvinces().then((provs) => {
      setProvinces(filterLocations(provs, limit == null ? void 0 : limit.provinces));
    });
    const { provinceCode, districtCode } = defaultAddress;
    if (provinceCode) {
      locationService.getDistrictsByProvinceCode(provinceCode).then((allDistricts) => {
        setDistricts(filterLocations(allDistricts, limit == null ? void 0 : limit.districts));
      });
    }
    if (districtCode) {
      locationService.getWardsByDistrictCode(districtCode).then((allWards) => {
        setWards(filterLocations(allWards, limit == null ? void 0 : limit.wards));
      });
    }
  }, []);
  const isValidLocation = () => {
    return (province == null ? void 0 : province.code) && (district == null ? void 0 : district.code) && ((ward == null ? void 0 : ward.code) || !wards.length);
  };
  const onProvinceChanged = async (item) => {
    setProvince(item);
    setDistrict(null);
    setWard(null);
    if (item) {
      const allDistricts = await locationService.getDistrictsByProvinceCode(item.code);
      setDistricts(filterLocations(allDistricts, limit == null ? void 0 : limit.districts));
      setWards([]);
    } else {
      setProvince(null);
      setDistricts([]);
      setWards([]);
    }
  };
  const onDistrictChanged = async (item) => {
    setDistrict(item);
    setWard(null);
    if (item) {
      const allWards = await locationService.getWardsByDistrictCode(item.code);
      setWards(filterLocations(allWards, limit == null ? void 0 : limit.wards));
    } else {
      setDistrict(null);
      setWards([]);
    }
  };
  const onWardChanged = (item) => {
    setWard(item);
  };
  const setLocation = ({
    provinceCode,
    districtCode,
    wardCode
  }) => {
    const province2 = provinces.find((p) => p.code === provinceCode);
    if (!province2)
      return;
    (async function getDetailAndSetLocation() {
      const [districts2 = [], wards2 = []] = await Promise.all([
        locationService.getDistrictsByProvinceCode(provinceCode),
        locationService.getWardsByDistrictCode(districtCode)
      ]);
      const district2 = districts2.find((d) => d.code === districtCode);
      const ward2 = wards2.find((w) => w.code === wardCode);
      setDistricts(districts2);
      setDistrict(district2);
      setWards(wards2);
      setWard(ward2);
      setProvince(province2);
    })();
  };
  return {
    isValidLocation,
    wards,
    provinces,
    districts,
    ward,
    province,
    district,
    onProvinceChanged,
    onDistrictChanged,
    onWardChanged,
    setLocation
  };
};
const useAddressForm = ({
  editingAddress,
  locationService,
  userService,
  platformCode,
  platformId,
  locationsLimit = {},
  requireEmail = false,
  allowAddDefaultAdress = false
}) => {
  const [place, setPlace] = useState({
    lat: void 0,
    long: void 0
  });
  const defaultAddress = __spreadProps(__spreadValues({
    name: "",
    telephone: "",
    address: "",
    email: "",
    provinceCode: "",
    districtCode: "",
    wardCode: "",
    isDefault: false
  }, editingAddress), {
    longitude: (editingAddress == null ? void 0 : editingAddress.longitude) || null,
    latitude: (editingAddress == null ? void 0 : editingAddress.latitude) || null
  });
  useEffect(() => {
    if (!!editingAddress && !!editingAddress.latitude && !!editingAddress.longitude) {
      setPlace({
        long: Number(editingAddress.longitude),
        lat: Number(editingAddress.latitude)
      });
    }
  }, [editingAddress]);
  const locationState = useLocations(defaultAddress, locationService, locationsLimit);
  const { district, ward, province } = locationState;
  const [isDefault, setIsDefault] = useState(defaultAddress.isDefault || allowAddDefaultAdress);
  const [message, setMessage] = useState({
    success: false,
    content: ""
  });
  const [loading, setLoading] = useState(false);
  const submitForm = async (formValues) => {
    if (loading)
      return;
    setLoading(true);
    try {
      const input = __spreadProps(__spreadValues({}, formValues), {
        name: formValues.name.trim(),
        telephone: formValues.telephone.trim(),
        address: formValues.address.trim(),
        provinceCode: province.code,
        districtCode: district.code,
        wardCode: ward.code,
        platformCode,
        platformId,
        provinceName: province.name,
        districtName: district.name,
        wardName: ward.name,
        isDefault: formValues.isDefault || isDefault
      });
      for (const [key, value] of Object.entries(input)) {
        if (value === "" || value === null) {
          delete input[key];
        }
      }
      input.email = !!formValues.email ? formValues.email.trim() : null;
      input.note = formValues.note;
      input.longitude = place.long ? `${place.long}` : "";
      input.latitude = place.lat ? `${place.lat}` : "";
      let res;
      if (editingAddress && (editingAddress.id || editingAddress.addressId)) {
        await userService.updateAddress(editingAddress.id || editingAddress.addressId, input);
        res = __spreadValues(__spreadValues({}, editingAddress), input);
      } else {
        res = await userService.createAddress(input);
      }
      return __spreadValues({
        addressId: res.addressId || res.id
      }, res);
    } catch (e) {
      logger.error(e);
      setMessage({
        success: false,
        content: t("C\xF3 l\u1ED7i x\u1EA3y ra. Vui l\xF2ng th\u1EED l\u1EA1i sau")
      });
      throw e;
    } finally {
      setLoading(false);
    }
  };
  const validateForm = (values) => {
    var _a, _b, _c;
    const errors = {};
    if (!((_a = values.name) == null ? void 0 : _a.trim())) {
      errors.name = t("Vui l\xF2ng nh\u1EADp t\xEAn ng\u01B0\u1EDDi nh\u1EADn");
    }
    if ((_b = values.telephone) == null ? void 0 : _b.trim()) {
      if (!isValidPhoneFormat(values.telephone)) {
        errors.telephone = t("S\u1ED1 \u0111i\u1EC7n tho\u1EA1i kh\xF4ng \u0111\xFAng \u0111\u1ECBnh d\u1EA1ng");
      }
    } else {
      errors.telephone = t("Vui l\xF2ng nh\u1EADp \u0111\xFAng s\u1ED1 \u0111i\u1EC7n tho\u1EA1i ng\u01B0\u1EDDi nh\u1EADn");
    }
    if ((_c = values.email) == null ? void 0 : _c.trim()) {
      if (!isValidEmailFormat(values.email)) {
        errors.email = t("\u0110\u1ECBa ch\u1EC9 email kh\xF4ng \u0111\xFAng \u0111\u1ECBnh d\u1EA1ng");
      }
    } else if (requireEmail) {
      errors.email = t("Tr\u01B0\u1EDDng b\u1EAFt bu\u1ED9c");
    }
    if (!values.address) {
      errors.address = t("Vui l\xF2ng nh\u1EADp \u0111\u1ECBa ch\u1EC9 nh\u1EADn h\xE0ng");
    }
    return errors;
  };
  return __spreadValues({
    submitForm,
    validateForm,
    message,
    isDefault,
    setIsDefault,
    loading,
    initialFormValues: defaultAddress,
    setPlace
  }, locationState);
};

export { useAddressForm };
